/**
 * 出勤表(その日の席・割振・時間。2026-09-12)。現行「【日報2609】コールセンター」のシフト表タブと同じ並び。
 *  - 管理者: その日の出勤を作る(席番号・割振・開始/終了・休憩)。上に時間帯ごとの稼働人数が出る
 *  - operator: 自分の行だけを見る
 * 目標取得数 = 稼働 × 2.0件/h(現行シートの値)。「アポ/h」は直近14日の実績(見込取得数/h)。
 * ※「シフト提出」「シフト表(月間)」は ShiftTabs.tsx の別タブ。
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import DateField from '@core/components/DateField';
import { IconClose } from '@core/components/icons';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { PAY_TYPE_LABELS, WORK_STYLE_LABELS, type Company, type DateStr, type Operator, type PayType, type WorkStyle } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperators, primaryStyle } from '@core/masters/operatorRepo';
import { addDays, apphOf, fmtNum, staffMetrics, sumAgg } from '@core/dashboard/calc';
import { fetchMonthTargets, fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import {
  SHIFT_SLOTS,
  TARGET_PER_HOUR,
  businessOfBbId,
  boundHoursOf,
  fetchShift,
  headcountBySlot,
  inSlot,
  newShiftRowKey,
  saveShift,
  targetCountOf,
  workedHoursOf,
  type ShiftRow,
} from './shiftRepo';
import { fetchSubmissions, type ShiftSubmission } from './shiftSubmitRepo';

const slotLabel = (s: number) => (Number.isInteger(s) ? String(s) : `${Math.floor(s)}½`);
const hourOptions = Array.from({ length: 25 }, (_, i) => 8 + i * 0.5).filter((h) => h <= 20);

export default function AttendanceTab() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const date = (params.get('date') ?? todayStr()) as DateStr;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ShiftRow[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [apph, setApph] = useState<Map<string, number>>(new Map());
  const [dirty, setDirty] = useState(false);
  const [addOp, setAddOp] = useState('');
  const [subs, setSubs] = useState<ShiftSubmission[]>([]);
  const [fromSubmission, setFromSubmission] = useState(false);
  const savedRef = useRef('');

  const guard = useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = addDays(date, -14) as DateStr;
      const [shift, cs, ops, sums, tg, sb] = await Promise.all([
        fetchShift(date),
        fetchCompanies(),
        fetchOperators(),
        fetchSummariesRange(from, date),
        fetchMonthTargets(date.slice(0, 7)),
        fetchSubmissions(date.slice(0, 7)),
      ]);
      setSubs(sb);
      setCompanies(cs);
      setOperators(ops);
      const map = new Map(cs.map((c) => [c.id, c]));
      const metrics = sums.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, tg, map)));
      const byOp = new Map<string, number>();
      for (const op of new Set(metrics.map((m) => m.op_id))) {
        const v = apphOf(sumAgg(metrics.filter((m) => m.op_id === op)));
        if (v !== null) byOp.set(op, v);
      }
      setApph(byOp);
      const saved = shift?.rows ?? [];
      if (saved.length > 0) {
        setRows(saved);
        savedRef.current = JSON.stringify(saved);
        setDirty(false);
        setFromSubmission(false);
      } else {
        // まだ作っていない日は、その日のシフト提出から自動で並べる(保存するまでは仮)
        const day = date.slice(8, 10);
        const auto = sb.flatMap((s) => {
          const d = s.days[day];
          if (!d) return [];
          const op = ops.find((o) => o.id === s.op_id);
          const acc = op?.bb_accounts.find((a) => a.bb_id === s.bb_id) ?? op?.bb_accounts[0];
          return [
            {
              key: newShiftRowKey(),
              op_id: s.op_id,
              bb_id: s.bb_id || (acc?.bb_id ?? ''),
              name: s.name || (op?.name ?? ''),
              work_style: acc?.work_style ?? null,
              company_id: null,
              seat: '',
              start: d.start,
              end: d.end,
              break_hours: 0,
              submitted_hours: Math.max(0, Math.round((d.end - d.start) * 2) / 2),
              note: '',
            } as ShiftRow,
          ];
        });
        setRows(auto);
        // 自動で並べただけでは「変更あり」にしない(何も触っていないのに離脱確認が出るのを防ぐ)
        savedRef.current = JSON.stringify(auto);
        setDirty(false);
        setFromSubmission(auto.length > 0);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [date, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (key: string, p: Partial<ShiftRow>) => {
    setRows((prev) => {
      const next = prev.map((r) => (r.key === key ? { ...r, ...p } : r));
      setDirty(JSON.stringify(next) !== savedRef.current);
      return next;
    });
  };

  const addRow = (opId: string) => {
    const op = operators.find((o) => o.id === opId);
    if (!op) return;
    const st = primaryStyle(op.bb_accounts);
    const row: ShiftRow = {
      key: newShiftRowKey(),
      op_id: op.id,
      bb_id: op.bb_accounts[0]?.bb_id ?? '',
      name: op.name,
      work_style: st.work_style,
      company_id: null,
      seat: '',
      start: 9,
      end: 18,
      break_hours: 0,
      submitted_hours: null,
      note: '',
    };
    setRows((prev) => {
      const next = [...prev, row];
      setDirty(true);
      return next;
    });
    setAddOp('');
  };

  const removeRow = (key: string) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      setDirty(JSON.stringify(next) !== savedRef.current);
      return next;
    });
  };

  /** 出勤形態×報酬区分(現行シートの並び順) */
  const STYLE_ORDER: { key: string; ws: WorkStyle; pt: PayType }[] = [
    { key: 'shinagawa_hourly', ws: 'shinagawa', pt: 'hourly' },
    { key: 'timee_hourly', ws: 'timee', pt: 'hourly' },
    { key: 'zaitaku_commission', ws: 'zaitaku', pt: 'commission' },
    { key: 'zaitaku_hourly', ws: 'zaitaku', pt: 'hourly' },
    { key: 'kanri_hourly', ws: 'kanri', pt: 'hourly' },
  ];
  const styleOf = useCallback(
    (r: ShiftRow) => {
      const op = operators.find((o) => o.id === r.op_id);
      const acc = op?.bb_accounts.find((a) => a.bb_id === r.bb_id) ?? op?.bb_accounts[0];
      const ws = acc?.work_style ?? r.work_style ?? 'zaitaku';
      const pt = acc?.pay_type ?? 'hourly';
      return { key: `${ws}_${pt}`, label: `${WORK_STYLE_LABELS[ws]}／${PAY_TYPE_LABELS[pt]}` };
    },
    [operators],
  );
  const visibleRows = useMemo(() => {
    const list = isAdmin ? rows : rows.filter((r) => r.op_id === user?.op_id);
    const idx = (r: ShiftRow) => {
      const i = STYLE_ORDER.findIndex((s) => s.key === styleOf(r).key);
      return i < 0 ? STYLE_ORDER.length : i;
    };
    return [...list].sort((a, b) => idx(a) - idx(b) || (a.seat || 'zzz').localeCompare(b.seat || 'zzz') || a.name.localeCompare(b.name, 'ja'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isAdmin, user?.op_id, styleOf]);

  /** その日のシフト提出から出勤者を入れる(現行フロー: シフト表から翌日の出勤者をコピペ) */
  const importFromSubmissions = () => {
    const day = date.slice(8, 10);
    const add: ShiftRow[] = [];
    for (const s of subs) {
      const d = s.days[day];
      if (!d) continue;
      if (rows.some((r) => r.op_id === s.op_id)) continue;
      const op = operators.find((o) => o.id === s.op_id);
      const acc = op?.bb_accounts.find((a) => a.bb_id === s.bb_id) ?? op?.bb_accounts[0];
      add.push({
        key: newShiftRowKey(),
        op_id: s.op_id,
        bb_id: s.bb_id || (acc?.bb_id ?? ''),
        name: s.name || (op?.name ?? ''),
        work_style: acc?.work_style ?? null,
        company_id: null,
        seat: '',
        start: d.start,
        end: d.end,
        break_hours: 0,
        submitted_hours: Math.max(0, Math.round((d.end - d.start) * 2) / 2),
        note: '',
      });
    }
    if (add.length === 0) {
      toast('この日のシフト提出はありません');
      return;
    }
    setRows((prev) => {
      const next = [...prev, ...add];
      setDirty(true);
      return next;
    });
    toast(`シフト提出から ${add.length}名を入れました`);
  };
  const head = useMemo(() => headcountBySlot(rows), [rows]);
  const totals = useMemo(() => {
    const worked = rows.reduce((s, r) => s + workedHoursOf(r), 0);
    // 目標は行ごとに架電内容で係数が違う(給湯器 2.0件/h・不用品 3時間1件)ので、行の目標を足す
    const target = rows.reduce((s, r) => s + targetCountOf(r, businessOfBbId(operators, r.bb_id)), 0);
    return { worked, target: Math.round(target * 10) / 10, people: rows.length };
  }, [rows, operators]);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await saveShift(date, rows, user.id);
      savedRef.current = JSON.stringify(rows);
      setDirty(false);
      setFromSubmission(false);
      toast('出勤表を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const setDate = (d: DateStr) => {
    const next = new URLSearchParams(params);
    next.set('date', d);
    setParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="empty">読み込み中…</div>
      </div>
    );
  }

  return (
    <>
      {/* 未保存のまま離れるときの確認。これを描いていないと遷移が止まったままになる(2026-09-12 修正) */}
      {guard.modal}
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          出勤表
          <DateField value={date} onChange={setDate} />
          <span className="hint" style={{ fontWeight: 400 }}>
            {date.replaceAll('-', '/')}
            {weekdayLabel(date)} ／ {totals.people}名 ／ 稼働 {fmtNum(totals.worked, 1)}h ／ 目標 {fmtNum(totals.target, 1)}件
          </span>
          {isAdmin && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn-line" onClick={importFromSubmissions}>
                シフト提出から入れる
              </button>
              <button className="btn" disabled={(!dirty && !fromSubmission) || saving} onClick={() => void save()}>
                {saving ? '保存中…' : '保存'}
              </button>
            </span>
          )}
        </h3>

        {fromSubmission && (
          <p className="hint" style={{ marginBottom: 8 }}>
            この日の出勤表はまだ保存されていません。<b>シフト提出から自動で並べています</b>。席番号と割振を入れて「保存」してください。
          </p>
        )}
        {rows.length === 0 ? (
          <div className="empty">
            この日はシフト提出がありません{isAdmin ? '。下の「スタッフを追加」から手で足せます' : ''}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="fit-table shift-table">
              <thead>
                <tr>
                  <th style={{ width: 52 }}>席</th>
                  <th style={{ width: 110 }}>割振</th>
                  <th style={{ width: 120 }}>スタッフ名</th>
                  <th className="num" style={{ width: 56 }}>アポ/h</th>
                  <th style={{ width: 76 }}>業務形態</th>
                  <th style={{ width: 72 }}>BB ID</th>
                  <th className="num" style={{ width: 62 }}>開始</th>
                  <th className="num" style={{ width: 62 }}>終了</th>
                  {SHIFT_SLOTS.map((s) => (
                    <th key={s} className="num slot">{slotLabel(s)}</th>
                  ))}
                  <th className="num" style={{ width: 48 }}>拘束</th>
                  <th className="num" style={{ width: 48 }}>稼働</th>
                  <th className="num" style={{ width: 48 }}>休憩</th>
                  <th className="num" style={{ width: 56 }}>目標</th>
                  {isAdmin && <th style={{ width: 34 }} />}
                </tr>
              </thead>
              <tbody>
                <tr className="shift-head">
                  <th colSpan={8}>時間帯ごとの人数</th>
                  {head.map((n, i) => (
                    <td key={i} className="num">{n || ''}</td>
                  ))}
                  <td colSpan={isAdmin ? 5 : 4} />
                </tr>
                {visibleRows.map((r, i) => (
                  <Fragment key={r.key}>
                    {(i === 0 || styleOf(visibleRows[i - 1]!).key !== styleOf(r).key) && (
                      <tr className="shift-group">
                        <th colSpan={isAdmin ? 33 : 32}>{styleOf(r).label}</th>
                      </tr>
                    )}
                  <tr>
                    <td>{isAdmin ? <input value={r.seat} onChange={(e) => patch(r.key, { seat: e.target.value })} style={{ width: '100%' }} /> : r.seat}</td>
                    <td>
                      {isAdmin ? (
                        <select value={r.company_id ?? ''} onChange={(e) => patch(r.key, { company_id: e.target.value || null })} style={{ width: '100%' }}>
                          <option value="">未定</option>
                          {companies.filter((c) => c.status === 'active').map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      ) : (
                        (companies.find((c) => c.id === r.company_id)?.name ?? '未定')
                      )}
                    </td>
                    <td>{r.name}</td>
                    <td className="num">{fmtNum(apph.get(r.op_id) ?? null, 1)}</td>
                    <td className="hint">{r.work_style ? WORK_STYLE_LABELS[r.work_style] : ''}</td>
                    <td className="hint">{r.bb_id}</td>
                    <td className="num">
                      {isAdmin ? (
                        <select value={r.start ?? ''} onChange={(e) => patch(r.key, { start: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: '100%' }}>
                          <option value="">—</option>
                          {hourOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      ) : (r.start ?? '—')}
                    </td>
                    <td className="num">
                      {isAdmin ? (
                        <select value={r.end ?? ''} onChange={(e) => patch(r.key, { end: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: '100%' }}>
                          <option value="">—</option>
                          {hourOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      ) : (r.end ?? '—')}
                    </td>
                    {SHIFT_SLOTS.map((s) => (
                      <td key={s} className={`num slot${inSlot(r, s) ? ' on' : ''}`} />
                    ))}
                    <td className="num">{fmtNum(boundHoursOf(r), 1)}</td>
                    <td className="num">{fmtNum(workedHoursOf(r), 1)}</td>
                    <td className="num">
                      {isAdmin ? (
                        <select value={r.break_hours} onChange={(e) => patch(r.key, { break_hours: Number(e.target.value) })} style={{ width: '100%' }}>
                          {[0, 0.5, 1, 1.5, 2].map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      ) : (r.break_hours || '')}
                    </td>
                    <td className="num">{fmtNum(targetCountOf(r, businessOfBbId(operators, r.bb_id)), 1)}</td>
                    {isAdmin && (
                      <td className="num">
                        <button className="icon-btn" title="この行を削除" onClick={() => removeRow(r.key)}>
                          <IconClose size={12} />
                        </button>
                      </td>
                    )}
                  </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isAdmin && (
          <div className="toolbar" style={{ marginTop: 10 }}>
            <div className="tool-filter">
              <select
                value={addOp}
                onChange={(e) => {
                  setAddOp(e.target.value);
                  if (e.target.value) addRow(e.target.value);
                }}
                style={{ minWidth: 240 }}
              >
                <option value="">スタッフを追加…</option>
                {operators
                  .filter((o) => o.status === 'active' && !rows.some((r) => r.op_id === o.id))
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}({o.op_code})
                    </option>
                  ))}
              </select>
              <span className="hint">目標取得数は 稼働 × {TARGET_PER_HOUR}件/h(不用品は3時間で1件)で計算します</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
